import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileDown, FileUp, Trash2, Pencil, AlertTriangle, Boxes } from "lucide-react";
import { exportToExcel } from "@/lib/excel";
import { tableToPdf } from "@/lib/pdf";
import { usePinPrompt } from "@/components/app/PinPrompt";
import SmartImport from "@/components/app/SmartImport";

const daysTo = (d) => Math.ceil((new Date(d) - new Date(new Date().toDateString())) / 86400000);

function ExpiryChip({ batches }) {
  const dated = (batches || []).filter((b) => b.expiry_date && b.qty > 0).sort((a, b) => a.expiry_date.localeCompare(b.expiry_date));
  if (!dated.length) return <span className="text-slate-300">—</span>;
  const d = daysTo(dated[0].expiry_date);
  const cls = d < 0 ? "chip-red" : d <= 30 ? "chip-amber" : "chip-green";
  return <span className={`chip ${cls}`} title={dated.map((b) => `${b.batch_no}: ${b.qty} · exp ${b.expiry_date}`).join("\n")}>{d < 0 ? "Expired" : `${dated[0].expiry_date}`}{dated.length > 1 ? ` +${dated.length - 1}` : ""}</span>;
}

const empty = {
  item_type: "goods", batch_no: "", expiry_date: "", mfg_date: "", warehouse_id: "", bin: "",
  name: "", sku: "", barcode: "", category: "", unit: "PCS",
  sale_price: 0, purchase_price: 0, gst_rate: 0, hsn: "",
  stock: 0, min_stock: 0, max_stock: 0, reorder_level: 0,
  avg_daily_usage: 0, ordering_cost: 0, holding_cost: 0,
};

export default function Items() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const pinPrompt = usePinPrompt();

  const load = async () => {
    const { data } = await api.get(`/items${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/warehouses").then(({ data }) => setWarehouses(data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);
  const bins = warehouses.find((w) => w._id_str === f.warehouse_id)?.bins || [];
  const isService = f.item_type === "service";

  const save = async () => {
    await pinPrompt();
    try {
      if (editingId) await api.patch(`/items/${editingId}`, f);
      else await api.post("/items", f);
      toast.success("Saved");
      setOpen(false); setF(empty); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete item?")) return;
    await pinPrompt();
    try { await api.delete(`/items/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const F = (k, l, extra = {}) => (
    <div><Label>{l}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: extra.type === "number" ? +e.target.value : e.target.value })} {...extra} /></div>
  );

  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="overline">Inventory</div>
          <h1 className="text-3xl font-bold tracking-tight">Items & Stock</h1>
        </div>
        <div className="flex gap-2 flex-wrap no-print">
          <Input placeholder="Search name / SKU / barcode..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-64" data-testid="items-search" />
          <Button variant="outline" onClick={load}>Filter</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="items-import-btn"><FileUp size={14} /> Import (any Excel)</Button>
          <Button variant="outline" onClick={() => exportToExcel(items, "items.xlsx")}><FileDown size={14} /> Excel</Button>
          <Button variant="outline" data-testid="items-pdf" onClick={() => tableToPdf({
            title: "Items / Stock",
            subtitle: `${items.length} items`,
            columns: [
              { key: "name", label: "Name", w: 130 },
              { key: "sku", label: "SKU", w: 70 },
              { key: "category", label: "Cat.", w: 70 },
              { key: "stock", label: "Stock", num: true, w: 60 },
              { key: "min_stock", label: "Min", num: true, w: 50 },
              { key: "sale_price", label: "Sale", num: true, w: 70 },
              { key: "purchase_price", label: "Purch", num: true, w: 70 },
              { key: "gst_rate", label: "GST%", num: true, w: 50 },
            ],
            rows: items,
            filename: "items.pdf",
          })}><FileDown size={14} /> PDF</Button>
          <Button className="btn-primary" onClick={() => { setF(empty); setEditingId(null); setOpen(true); }} data-testid="item-add-btn"><Plus size={14} /> Add Item</Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="acc-table w-full">
          <thead><tr><th>Name</th><th>SKU</th><th>Category</th><th>Unit</th><th className="num">Stock</th><th className="num">Min</th><th className="num">Reorder</th><th className="num">EOQ</th><th>Expiry</th><th>Status</th><th className="num">Sale ₹</th><th className="num">Purch ₹</th><th className="num">GST</th><th>Actions</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={14} className="text-center py-6 text-muted-foreground">No items. Add your first item.</td></tr>}
            {items.map((it) => {
              const svc = it.item_type === "service";
              const out = !svc && it.stock <= 0;
              const low = !svc && (out || (it.min_stock > 0 && it.stock <= it.min_stock));
              const eoq = Number(it.eoq || 0);
              const reorderTrigger = it.reorder_level > 0 ? it.reorder_level : (eoq > 0 ? eoq : it.min_stock);
              const reorderNow = reorderTrigger > 0 && it.stock <= reorderTrigger;
              return (
                <tr key={it._id_str} data-testid={`item-row-${it._id_str}`}>
                  <td className="font-semibold flex items-center gap-2">{low && <AlertTriangle size={12} className="text-amber-600" />} {it.name}{svc && <span className="chip chip-slate text-[10px]">Service</span>}</td>
                  <td className="font-mono text-xs">{it.sku || "—"}</td>
                  <td>{it.category || "—"}</td>
                  <td>{it.unit}</td>
                  <td className={`num ${low ? "text-[#991B1B] font-bold" : ""}`}>{svc ? "—" : it.stock}{it.in_transit > 0 && <div className="text-[10px] text-blue-700">+{it.in_transit} in transit</div>}</td>
                  <td className="num">{it.min_stock}</td>
                  <td className="num">{it.reorder_level}</td>
                  <td className="num" title={eoq > 0 ? `Order ${eoq} units per PO for lowest total cost` : "Set avg daily usage, ordering cost & holding cost to calculate"}>
                    {eoq > 0 ? <span className="font-mono font-semibold text-[#064E3B]">{eoq}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td><ExpiryChip batches={it.batches} /></td>
                  <td>
                    {svc && <span className="chip chip-slate">Service</span>}
                    {out && <span className="chip chip-red" data-testid={`item-status-out-${it._id_str}`}>No available stock</span>}
                    {low && !out && <span className="chip chip-red" data-testid={`item-status-low-${it._id_str}`}>Low stock</span>}
                    {!svc && !low && reorderNow && <span className="chip chip-amber" data-testid={`item-status-reorder-${it._id_str}`}>Reorder now</span>}
                    {!svc && !low && !reorderNow && it.stock > 0 && <span className="chip chip-green">In stock</span>}
                  </td>
                  <td className="num">{fmtINR(it.sale_price)}</td>
                  <td className="num">{fmtINR(it.purchase_price)}</td>
                  <td className="num">{it.gst_rate}%</td>
                  <td>
                    <div className="flex gap-1">
                      <button className="p-1 hover:bg-slate-100 rounded" onClick={() => { setF({ ...empty, ...it }); setEditingId(it._id_str); setOpen(true); }} data-testid={`item-edit-${it._id_str}`}><Pencil size={14} /></button>
                      <button className="p-1 hover:bg-red-50 text-red-600 rounded" onClick={() => del(it._id_str)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Type</Label>
              <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.item_type} onChange={(e) => setF({ ...f, item_type: e.target.value })} data-testid="item-type-select">
                <option value="goods">Goods (stock tracked)</option>
                <option value="service">Service (no stock)</option>
              </select>
            </div>
            {F("name", "Name*")}
            {F("sku", "SKU")}
            {F("barcode", "Barcode")}
            {F("category", "Category")}
            {F("unit", "Unit")}
            {F("hsn", "HSN")}
            {F("sale_price", "Sale Price", { type: "number" })}
            {F("purchase_price", "Purchase Price", { type: "number" })}
            {F("gst_rate", "GST %", { type: "number" })}
            {!isService && F("stock", editingId ? "Current Stock" : "Opening Stock", { type: "number", "data-testid": "item-stock-input" })}
            {F("min_stock", "Min Stock", { type: "number" })}
            {F("max_stock", "Max Stock", { type: "number" })}
            {F("reorder_level", "Reorder Level", { type: "number" })}
            {F("avg_daily_usage", "Avg daily usage (units/day)", { type: "number" })}
            {F("ordering_cost", "Ordering cost / order", { type: "number" })}
            {F("holding_cost", "Holding cost / unit / yr", { type: "number" })}
          </div>
          {!isService && !editingId && (
            <div className="border-t border-border pt-3">
              <div className="overline mb-2">Opening stock batch, expiry & location</div>
              <div className="grid grid-cols-3 gap-3">
                {F("batch_no", "Batch / Lot No", { "data-testid": "item-batch-input" })}
                {F("expiry_date", "Expiry Date", { type: "date", "data-testid": "item-expiry-input" })}
                {F("mfg_date", "Mfg Date", { type: "date" })}
                <div><Label>Warehouse</Label>
                  <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.warehouse_id || ""} onChange={(e) => setF({ ...f, warehouse_id: e.target.value, bin: "" })} data-testid="item-warehouse-select">
                    <option value="">— none —</option>
                    {warehouses.map((w) => <option key={w._id_str} value={w._id_str}>{w.name}</option>)}
                  </select>
                </div>
                <div><Label>Bin</Label>
                  <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.bin || ""} onChange={(e) => setF({ ...f, bin: e.target.value })} data-testid="item-bin-select">
                    <option value="">— none —</option>
                    {bins.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
          {editingId && (f.batches || []).length > 0 && (
            <div className="text-xs border border-border rounded p-2" data-testid="item-batches-list">
              <div className="overline mb-1">Batches</div>
              {f.batches.map((b) => <div key={b.batch_no} className="flex justify-between font-mono"><span>{b.batch_no}</span><span>{b.qty} · exp {b.expiry_date || "—"}</span></div>)}
            </div>
          )}
          <div className="text-xs text-muted-foreground">EOQ auto-computed from usage, ordering cost, holding cost. New expiry batches are added when you record a Purchase with batch / expiry.</div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="item-form-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      <SmartImport target="items" open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
    </div>
  );
}
